/**
 * ChartBlock — renders a validated ```chart spec (#2219) as a recharts column
 * chart PLUS an always-rendered, accessible values table.
 *
 * Accessibility model (ONE model, no mixing): the recharts SVG is DECORATIVE —
 * its container is ``aria-hidden``, with no ``role="img"``/``aria-label``. The
 * accessible surface is the visible ``<figcaption>`` title (a normal text
 * element OUTSIDE the hidden container), the collapsible ``<details>`` values
 * table, and the prose total the assistant states in the reply itself.
 *
 * Security: ``title``/``unit``/``label`` are attacker-influenceable (prompt
 * injection via document text). They land ONLY as React text children / SVG
 * ``<text>`` (recharts renders labels as SVG text) — never markup, never an
 * attribute other than the ones here — so they are XSS-inert by construction.
 *
 * recharts does not render under jsdom (zero width → no SVG), so the values
 * table is the test surface; the pure parser (``chartSpec.ts``) carries the
 * validation logic.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { formatEur } from '../../utils/formatEur';
import type { ChartSpec } from './chartSpec';

// House chart conventions (MarginRankChart.tsx): the positive-series blue, the
// faint grid, tick fontSize 11, no animation, ResponsiveContainer + fixed height.
const POSITIVE = '#2563eb';
const GRID = '#eef2f7';
const CHART_HEIGHT = 220;

// de-DE quantity formatter (Deckungsbeitrag precedent). EUR routes through the
// shared currency formatter; every other unit is appended as a plain string.
const NUM = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

const isEurUnit = (unit?: string): boolean => unit === '€' || unit === 'EUR';

/** Clip a long label so a 10k-char injection can't blow up layout (MarginRankChart:51-52). */
const clip = (label: string, max: number): string => (label.length > max ? `${label.slice(0, max - 1)}…` : label);

function formatValue(value: number, unit?: string): string {
  if (isEurUnit(unit)) return formatEur(value);
  return unit ? `${NUM.format(value)} ${unit}` : NUM.format(value);
}

export const ChartBlock: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { t } = useTranslation();
  const { title, unit, points } = spec;

  return (
    <figure className="chat-chart" data-testid="chat-chart">
      {title && <figcaption className="chat-chart__title">{clip(title, 80)}</figcaption>}
      {/* The recharts SVG is decorative — hidden from assistive tech. The
          numbers stay reachable via the values table below. */}
      <div className="chat-chart__canvas" aria-hidden="true">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickFormatter={(label: string) => clip(String(label), 12)}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(value: number) => NUM.format(Number(value))}
              width={56}
              axisLine={false}
              tickLine={false}
            />
            <Bar dataKey="value" fill={POSITIVE} isAnimationActive={false} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <details className="chat-chart__data">
        <summary>{t('chat.ki.chart.showData', 'Daten anzeigen')}</summary>
        <div className="chat-bubble__table-wrap">
          <table className="chat-chart__table">
            <tbody>
              {points.map((point, index) => (
                <tr key={index}>
                  <th scope="row">{point.label}</th>
                  <td>{formatValue(point.value, unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
};

export default ChartBlock;
